import React, { useEffect } from 'react';
import useGameStore from './store/gameStore';
import { useSocket } from './hooks/useSocket';
import { useAutoBet } from './hooks/useAutoBet';
import GameCanvas from './components/game/GameCanvas';
import BetPanel from './components/game/BetPanel';
import Header from './components/layout/Header';
import ResultPopup from './components/game/ResultPopup';
import HistoryPanel from './components/game/HistoryPanel';
import { getGameById } from './config/gamesCatalog';
import './styles/app.css';

const SERVER_URL = process.env.REACT_APP_SERVER_URL || 'http://165.232.151.14:4000';

/**
 * Returns true only when all required game params are present AND
 * the gameId matches a known entry in the games catalog.
 */
function hasValidGameParams() {
  const p = new URLSearchParams(window.location.search);
  const gameId = p.get('gameId');
  return !!(
    gameId &&
    p.get('sessionId') &&
    p.get('playerId') &&
    p.get('mode') &&
    getGameById(gameId)
  );
}

// ─── Loading screen ──────────────────────────────────────────────────────────
/** SunsoftGaming's neon "S" mark, matching the brand at sunsoftgaming.com. */
function SunsoftMark() {
  return (
    <svg className="game-loader__mark" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="sg-loader-neon" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#00F5FF" />
          <stop offset="50%" stopColor="#7C3AED" />
          <stop offset="100%" stopColor="#FF2E88" />
        </linearGradient>
        <filter id="sg-loader-glow">
          <feGaussianBlur stdDeviation="2.5" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>
      <rect width="64" height="64" rx="14" fill="#0B0F1A" />
      <rect
        x="8"
        y="8"
        width="48"
        height="48"
        rx="12"
        fill="none"
        stroke="url(#sg-loader-neon)"
        strokeWidth="2.5"
        filter="url(#sg-loader-glow)"
      />
      <circle cx="32" cy="32" r="18" fill="url(#sg-loader-neon)" opacity="0.12" />
      <path
        d="M42 24 C38 20, 26 20, 24 26 C22 31, 27 32, 33 33 C39 34, 40 38, 36 40 C32 42, 24 41, 22 38"
        fill="none"
        stroke="#FFFFFF"
        strokeWidth="4"
        strokeLinecap="round"
        filter="url(#sg-loader-glow)"
      />
      <circle cx="44" cy="40" r="2.5" fill="#00F5FF" />
    </svg>
  );
}

function GameLoader() {
  return (
    <div className="game-loader">
      <div className="game-loader__brand">
        <SunsoftMark />
        <div className="game-loader__wordmark">SunsoftGaming</div>
      </div>
      <div className="game-loader__status">
        <div className="game-loader__spinner" />
        <div className="game-loader__text">Loading…</div>
      </div>
    </div>
  );
}

// ─── Game view (existing logic) ───────────────────────────────────────────────
function GameView() {
  const { initFromUrl, setGameConfig } = useGameStore();
  const gameState = useGameStore(s => s.gameState);
  const autoBet   = useGameStore(s => s.autoBet);
  const connected = useGameStore(s => s.connected);
  const { placeBet, takeStep, cashOut } = useSocket();
  const { startAutoBet, stopAutoBet } = useAutoBet({ placeBet, takeStep, cashOut });

  useEffect(() => {
    if (autoBet) return;
    if (gameState === 'round_started' || gameState === 'step_success') {
      const currentStep = useGameStore.getState().currentStep;
      const delay = gameState === 'round_started' ? 450 : currentStep === 1 ? 300 : 576;
      const timer = setTimeout(() => {
        const state = useGameStore.getState();
        if (!state.autoBet && (state.gameState === 'round_started' || state.gameState === 'step_success')) {
          takeStep();
        }
      }, delay);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameState, autoBet]);

  useEffect(() => {
    let wakeLock = null;

    const requestWakeLock = async () => {
      if (!('wakeLock' in navigator)) return;
      try { wakeLock = await navigator.wakeLock.request('screen'); } catch (_) {}
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') requestWakeLock();
    };

    requestWakeLock();
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      wakeLock?.release();
    };
  }, []);

  useEffect(() => {
    const { gameId } = initFromUrl();
    fetch(`${SERVER_URL}/api/game/${gameId}/config`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load game config');
        return res.json();
      })
      .then((config) => setGameConfig(config))
      .catch((err) => console.error('Game config load error:', err));
  }, [initFromUrl, setGameConfig]);

  if (!connected) return <GameLoader />;

  return (
    <div className="app-wrapper">
      <Header />
      <main className="app-main">
        <div className="game-layout">
          <div className="canvas-column">
            <GameCanvas />
          </div>
          <div className="panel-column">
            <BetPanel
              placeBet={placeBet}
              cashOut={cashOut}
              startAutoBet={startAutoBet}
              stopAutoBet={stopAutoBet}
            />
          </div>
          <div className="history-column">
            <HistoryPanel />
          </div>
        </div>
      </main>
      <ResultPopup />
    </div>
  );
}

// ─── Root router ──────────────────────────────────────────────────────────────
export default function App() {
  if (!hasValidGameParams()) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#060b14', color: '#94a3b8', fontFamily: 'sans-serif' }}>
        Missing or invalid game parameters.
      </div>
    );
  }
  return <GameView />;
}
